import { useEffect, useContext, useReducer, useState, Dispatch, SetStateAction } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import UserContext from "../../Context/UserProvider";
import { useApiService } from "../../services/ApiService/ApiService";
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
    CircularProgress
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

interface ProfilePageProps {
    error: [boolean, Dispatch<SetStateAction<boolean>>];
}

function reducer(state: any, action: any) {
    switch (action.type) {
        case "KM":
            return { option1: true, option2: false };
        case "MILES":
            return { option1: false, option2: true };
        default:
            return state;
    }
}

function ProfilePage({ error }: ProfilePageProps) {
    const { user } = useAuth0();
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const type = userInfo?.accountType || "FREE";
    const [state, dispatch] = useReducer(reducer, { option1: false, option2: false });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.title = "Profile";
    }, []);

    if (!user) {
        return null;
    }

    const switchUnitDistance = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/user_auth/profile/save', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    userProfile: { 
                        distanceUnit: state.option1 ? "KM" : "MILES" 
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update unit distance');
            }

            const data = await response.json();
            // Update the user profile in context if needed
        } catch (error) {
            console.error('Error updating unit distance:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container 
            maxWidth={false}
            sx={{ 
                pt: 3,
                width: {
                    xs: '100%',    // Full width on mobile
                    sm: '100%',    // Full width on tablet
                    md: '66.67%',  // 2/3 width on medium screens
                    lg: '33.33%',  // 1/3 width on large screens
                },
                mx: 'auto'
            }}
        >
            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            )}
            
            <Stack spacing={3}>
                <Paper 
                    elevation={0}
                    sx={{ p: 3, borderRadius: 1 }}
                >
                    <Stack spacing={3} alignItems="center">
                        <AccountCircleIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                        
                        <Stack spacing={2} width="100%">
                            <Typography variant="h6" fontWeight="bold">
                                ABOUT
                            </Typography>
                            <Stack spacing={1}>
                                <Typography variant="body1">
                                    Account: {type}
                                </Typography>
                                <Typography variant="body1">
                                    Preferred Unit: {userInfo.userProfile.distanceUnitDisplayName}
                                </Typography>
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" fontWeight="bold">
                                CONTACT
                            </Typography>
                            <Typography variant="body1">
                                Email: {user.email}
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper 
                    elevation={0}
                    sx={{ p: 3, borderRadius: 1 }}
                >
                    <Stack spacing={3}>
                        <Typography variant="h6">
                            Select Unit Distance
                        </Typography>

                        <ToggleButtonGroup
                            value={state.option1 ? "KM" : state.option2 ? "MILES" : null}
                            exclusive
                            onChange={(e, value) => {
                                if (value === "KM") {
                                    dispatch({ type: "KM" });
                                } else if (value === "MILES") {
                                    dispatch({ type: "MILES" });
                                }
                            }}
                            fullWidth
                        >
                            <ToggleButton value="KM">
                                Kilometers
                            </ToggleButton>
                            <ToggleButton value="MILES">
                                Miles
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Button
                            variant="contained"
                            onClick={switchUnitDistance}
                            disabled={!(state.option1 === true || state.option2 === true)}
                            fullWidth
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Paper>
            </Stack>
        </Container>
    );
}

export default ProfilePage;
